import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';
import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import DisableLinkConfirm, {
  DangerButton,
} from '@/components/ShareBasketModal/DisableLinkConfirm';
import { fetchSharedBaskets } from '@/lib/api';
import { formatSharedBasketDate } from '@/lib/sharedBasketDate';
import { sharedBasketQueryKeys } from '@/lib/sharedBasketQueryKeys';
import {
  SharedBasketRecipientMode,
  SharedBasketStatus,
  SharedBasketSummary,
} from '@/lib/types';
import { useAuthContext } from '@/contexts/AuthContext';
import { useShareBasket } from '@/hooks/useShareBasket';

const RecipientModeLabels: Record<
  SharedBasketRecipientMode,
  { id: string; defaultMessage: string }
> = {
  partner: {
    id: 'recipientModePartner',
    defaultMessage: 'A partner',
  },
  contacts: {
    id: 'recipientModeContacts',
    defaultMessage: 'Specific contacts',
  },
};

const StatusLabels: Record<
  SharedBasketStatus,
  { id: string; defaultMessage: string }
> = {
  active: { id: 'statusActive', defaultMessage: 'Active' },
  expired: { id: 'statusExpired', defaultMessage: 'Expired' },
  disabled: { id: 'statusDisabled', defaultMessage: 'Disabled' },
};

// Table needs a numeric `id`; a basket's key is its token, so the position
// in the response stands in.
type SharedLinkRow = SharedBasketSummary & { id: number };

const MySharedLinksModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { user } = useAuthContext();
  const [confirmingToken, setConfirmingToken] = useState<
    string | null
  >(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const { disable, isDisabling, disableError, clearDisableError } =
    useShareBasket();

  const {
    data: links,
    isPending,
    isError,
  } = useQuery({
    queryKey: [...sharedBasketQueryKeys.mine, user?.id],
    queryFn: fetchSharedBaskets,
    enabled: open,
    staleTime: 0,
    retry: false,
  });

  // Memoised: Table resets its sort whenever the `rows` identity changes.
  const rows: SharedLinkRow[] = useMemo(
    () =>
      (links ?? []).map((link, index) => ({ ...link, id: index })),
    [links],
  );

  // Nothing half-open should survive a close/reopen - least of all a confirm
  // box sitting on a row the user has since stopped looking at.
  useEffect(() => {
    if (!open) {
      setConfirmingToken(null);
      setCopiedToken(null);
      clearDisableError();
    }
  }, [open, clearDisableError]);

  const confirmingRow = rows.find(
    (link) => link.token === confirmingToken,
  );
  useEffect(() => {
    if (confirmingToken && confirmingRow?.status !== 'active') {
      setConfirmingToken(null);
    }
  }, [confirmingToken, confirmingRow]);

  const handleCopy = (token: string) => {
    navigator.clipboard
      ?.writeText(`${window.location.origin}/shared/${token}`)
      .then(
        () => setCopiedToken(token),
        () => setCopiedToken(null),
      );
  };

  const columns: ColumnDef<SharedLinkRow>[] = [
    {
      field: 'status',
      width: 110,
      align: 'left',
      label: <FormattedMessage id="status" defaultMessage="Status" />,
      render: (row) => (
        <StatusChip $status={row.status}>
          <FormattedMessage {...StatusLabels[row.status]} />
        </StatusChip>
      ),
    },
    {
      field: 'recipient_mode',
      width: 150,
      align: 'left',
      label: (
        <FormattedMessage
          id="sharedWith"
          defaultMessage="Shared with"
        />
      ),
      render: (row) => (
        <FormattedMessage
          {...RecipientModeLabels[row.recipient_mode]}
        />
      ),
    },
    {
      field: 'item_count',
      width: 80,
      align: 'center',
      label: <FormattedMessage id="files" defaultMessage="Files" />,
    },
    {
      field: 'created_at',
      width: 140,
      align: 'center',
      label: (
        <FormattedMessage id="created" defaultMessage="Created" />
      ),
      render: (row) => formatSharedBasketDate(row.created_at),
    },
    {
      field: 'expires_at',
      width: 140,
      align: 'center',
      label: (
        <FormattedMessage id="expires" defaultMessage="Expires" />
      ),
      // For a disabled link, when it was killed is the date that matters.
      render: (row) =>
        row.status === 'disabled' && row.disabled_at
          ? formatSharedBasketDate(row.disabled_at)
          : formatSharedBasketDate(row.expires_at),
    },
    {
      id: 'actions',
      label: (
        <FormattedMessage id="actions" defaultMessage="Actions" />
      ),
      align: 'center',
      render: (row) =>
        row.status === 'active' ? (
          <RowActions>
            <Button
              variant="text"
              customVariant="text-small"
              onClick={() => handleCopy(row.token)}
              aria-label="Copy link"
            >
              {copiedToken === row.token ? (
                <FormattedMessage
                  id="copied"
                  defaultMessage="Copied ✓"
                />
              ) : (
                <FormattedMessage
                  id="copyLink"
                  defaultMessage="Copy link"
                />
              )}
            </Button>
            <DangerButton
              variant="text"
              customVariant="text-small"
              onClick={() => {
                clearDisableError();
                setConfirmingToken(row.token);
              }}
              disabled={isDisabling}
              aria-label="Disable link"
            >
              <FormattedMessage
                id="disableLink"
                defaultMessage="Disable link"
              />
            </DangerButton>
          </RowActions>
        ) : null,
    },
  ];

  const renderBody = () => {
    if (isError) {
      return (
        <Centered role="alert">
          <FormattedMessage
            id="mySharedLinksError"
            defaultMessage="Could not load your shared links. Please try again."
          />
        </Centered>
      );
    }

    if (!isPending && rows.length === 0) {
      return (
        <Empty>
          <FormattedMessage
            id="mySharedLinksEmpty"
            defaultMessage="You haven't shared anything yet. Select files from Resources and use Share to create a link."
          />
        </Empty>
      );
    }

    return (
      <TableScroll>
        <Table<SharedLinkRow>
          rows={rows}
          columns={columns}
          loading={isPending}
          order="desc"
          orderBy="created_at"
          dense
          renderRowDetail={(row) =>
            confirmingToken === row.token ? (
              <ConfirmWrapper>
                <DisableLinkConfirm
                  error={disableError}
                  isDisabling={isDisabling}
                  onConfirm={() => disable(row.token)}
                  onCancel={() => setConfirmingToken(null)}
                />
              </ConfirmWrapper>
            ) : null
          }
        />
      </TableScroll>
    );
  };

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      isActionBusy={isDisabling}
      title={
        <FormattedMessage
          id="mySharedLinks"
          defaultMessage="My shared links"
        />
      }
    >
      <Sub>
        <FormattedMessage
          id="mySharedLinksSub"
          defaultMessage="Every link you've created. Disabling one stops it working for its recipients immediately and cannot be undone."
        />
      </Sub>
      {renderBody()}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)({
  width: '90%',
  maxWidth: 900,
});

const Sub = styled('p')(({ theme }) => ({
  margin: 0,
  marginBottom: theme.spacing(2),
  fontSize: 13,
  lineHeight: '18px',
  color: theme.palette.grey500,
}));

const Centered = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  color: theme.palette.grey500,
  fontSize: 14,
}));

const Empty = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  borderRadius: theme.radii.sm,
  border: `1px dashed ${theme.palette.grey300}`,
  color: theme.palette.grey500,
  fontSize: 14,
  lineHeight: '20px',
}));

// The endpoint is unpaginated, so the list is scrolled rather than trimmed.
const TableScroll = styled('div')(({ theme }) => ({
  maxHeight: '60vh',
  overflow: 'auto',
  'thead th': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  // Table sizes its first column for the select-all checkbox; with selection
  // off that would squeeze Status instead.
  'tr > th:first-of-type, tr > td:first-of-type': {
    width: 'auto',
    padding: theme.spacing(1),
  },
  'th, td': {
    whiteSpace: 'nowrap',
  },
}));

const RowActions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  whiteSpace: 'nowrap',
}));

const ConfirmWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 2),
  // A paragraph, unlike the cells above.
  whiteSpace: 'normal',
  textAlign: 'left',
}));

const STATUS_COLORS = {
  active: { bg: 'green50', fg: 'green700' },
  expired: { bg: 'grey100', fg: 'grey600' },
  disabled: { bg: 'red50', fg: 'red600' },
} as const;

const StatusChip = styled('span', {
  shouldForwardProp: (prop) => prop !== '$status',
})<{ $status: SharedBasketStatus }>(({ theme, $status }) => ({
  display: 'inline-block',
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.radii.xs,
  background: theme.palette[STATUS_COLORS[$status].bg],
  color: theme.palette[STATUS_COLORS[$status].fg],
  fontSize: 12,
  fontWeight: 600,
}));

export default MySharedLinksModal;
